import React, { SetStateAction, useCallback, useMemo, useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  FaArrowDown,
  FaArrowUp,
  FaPlus,
  FaQuestionCircle,
  FaTrash,
} from "react-icons/fa";
import DynamicInput from "components/dynamic/DynamicInput";
import Button from "components/button/Button";
import IconButton from "components/button/IconButton";
import { useTranslation } from "react-i18next";
import { ScopeType } from "types/globalTypes";
import { Field } from "components/dynamic/DynamicForm";

interface Props {
  name?: string;
  scope: ScopeType;
  isViewMode: boolean;
  setIsHelpModalOpen: (value: SetStateAction<boolean>) => void;
  required?: boolean;
  minItems?: number;
}

const ProcessStepItem = React.memo(
  ({
    index,
    totalLength,
    onMoveUp,
    onMoveDown,
    onRemove,
    namePrefix,
    scope,
    isViewMode,
    minItems,
  }: {
    index: number;
    totalLength: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onRemove: () => void;
    namePrefix: string;
    scope: ScopeType;
    isViewMode: boolean;
    minItems: number;
  }) => {
    const { t } = useTranslation();

    const fields: Field[] = useMemo(
      () => [
        {
          name: `${namePrefix}.title`,
          label: t("company.processSteps.stepTitle"),
          type: "text",
          required: true,
          disabled: isViewMode,
          placeholder: t("company.processSteps.stepTitlePlaceholder"),
        },
        {
          name: `${namePrefix}.description`,
          label: t("company.fields.description"),
          type: "textarea",
          required: true,
          disabled: isViewMode,
          rows: 3,
          placeholder: t("company.processSteps.stepDescriptionPlaceholder"),
        },
        {
          name: `${namePrefix}.image`,
          label: t("company.processSteps.stepImage"),
          type: "upload",
          disabled: isViewMode,
          multiple: false,
          required: true,
          accept: "image/*",
        },
      ],
      [isViewMode, namePrefix, t]
    );

    return (
      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-gray-50 shadow-sm dark:border-white/10 dark:bg-gray-800/40">
        {/* TOP BAR */}
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-white/70 px-3 py-2 dark:border-white/10 dark:bg-gray-900/30 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-xs font-bold text-white shadow-md sm:h-9 sm:w-9 sm:text-sm">
              {index + 1}
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-800 dark:text-white sm:text-base">
                {t("company.processSteps.step", {
                  current: index + 1,
                  total: totalLength,
                })}
              </h3>
              <p className="text-xs text-gray-500">
                {t("company.processSteps.stepOrder")}
              </p>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          {!isViewMode && (
            <div className="flex items-center justify-end gap-1">
              <IconButton
                title={t("actions.toTop")}
                handler={onMoveUp}
                icon={<FaArrowUp size={12} />}
                disabled={index === 0}
                className={`${
                  index === 0
                    ? "cursor-not-allowed opacity-40"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                } p-2 sm:p-2.5`}
              />

              <IconButton
                title={t("actions.toBottom")}
                handler={onMoveDown}
                icon={<FaArrowDown size={12} />}
                disabled={index === totalLength - 1}
                className={`${
                  index === totalLength - 1
                    ? "cursor-not-allowed opacity-40"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                } p-2 sm:p-2.5`}
              />

              <IconButton
                title={t("company.processSteps.stepDelete")}
                handler={onRemove}
                icon={<FaTrash size={12} />}
                disabled={totalLength <= minItems}
                className={`p-2 transition-colors ${
                  totalLength <= minItems
                    ? "cursor-not-allowed opacity-40"
                    : "text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                } sm:p-2.5`}
              />
            </div>
          )}
        </div>

        {/* FORM */}
        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
            <DynamicInput fields={fields} role={scope} />
          </div>
        </div>
      </div>
    );
  }
);

ProcessStepItem.displayName = "ProcessStepItem";

export default function ProcessStepsBuilder({
  name = "processSteps",
  scope,
  isViewMode,
  setIsHelpModalOpen,
  required = false,
  minItems = 1,
}: Props) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const {
    fields: items,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name,
    shouldUnregister: false,
    keyName: "_id",
  });

  const isEmpty = items.length === 0;
  const appendTimeoutRef = useRef<any | null>(null);

  const handleAppend = useCallback(() => {
    if (appendTimeoutRef.current) {
      clearTimeout(appendTimeoutRef.current);
      appendTimeoutRef.current = null;
    }

    const newItem = {
      title: "",
      description: "",
      image: "",
      _tempId: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    append(newItem);

    appendTimeoutRef.current = setTimeout(() => {
      appendTimeoutRef.current = null;
    }, 300);
  }, [append]);

  const handleRemove = useCallback(
    (index: number) => {
      if (index >= 0 && index < items.length && items.length > minItems) {
        remove(index);
      }
    },
    [remove, items.length, minItems]
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index > 0 && index < items.length) {
        move(index, index - 1);
      }
    },
    [move, items.length]
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= 0 && index < items.length - 1) {
        move(index, index + 1);
      }
    },
    [move, items.length]
  );

  const emptyState = useMemo(
    () => (
      <div className="rounded-3xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-white/10 dark:bg-gray-800/40 sm:p-12">
        <div className="mb-3 text-4xl sm:text-5xl">🏭</div>
        <h3 className="text-md font-bold text-gray-700 dark:text-gray-200">
          {t("company.processSteps.noItemsTitle")}
        </h3>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
          {t("company.processSteps.noItemsDescription")}
        </p>
        {required && minItems > 0 && (
          <p className="mt-2 text-xs text-red-500">
            {t("validation.minItemsRequired", { count: minItems })}
          </p>
        )}
        {!isViewMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            startIcon={<FaPlus size={12} />}
            onClick={handleAppend}
            className="mt-4 w-full sm:w-auto"
          >
            {t("company.processSteps.addButton")}
          </Button>
        )}
      </div>
    ),
    [t, required, minItems, isViewMode, handleAppend]
  );

  return (
    <div className="rounded-3xl border border-gray-200 bg-white/70 p-4 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/60 sm:p-5">
      {/* HEADER */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white sm:text-xl">
              {t("company.processSteps.title")}
              {required && <span className="mr-1 text-sm text-red-500">*</span>}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              {t("company.processSteps.description")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsHelpModalOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            title={t("company.processSteps.helpTooltip")}
          >
            <FaQuestionCircle size={18} />
          </button>
        </div>

        {!isViewMode && !isEmpty && (
          <Button
            type="button"
            size="sm"
            variant="primary"
            startIcon={<FaPlus size={13} />}
            onClick={handleAppend}
            className="w-full sm:w-auto"
          >
            {t("company.processSteps.addButton")}
          </Button>
        )}
      </div>

      {/* LIST OR EMPTY STATE  */}
      {isEmpty ? (
        emptyState
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {items.map((item, index) => {
            const uniqueKey = item._id || `step-${index}-${Date.now()}`;

            return (
              <ProcessStepItem
                key={uniqueKey}
                index={index}
                totalLength={items.length}
                onMoveUp={() => handleMoveUp(index)}
                onMoveDown={() => handleMoveDown(index)}
                onRemove={() => handleRemove(index)}
                namePrefix={`${name}.${index}`}
                scope={scope}
                isViewMode={isViewMode}
                minItems={minItems}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
